// UX-03/PGS-4/SEO-5 — the branded marketing 404. Mounted by App.js for every unknown PUBLIC
// url (and by SegmentRouteGate for an unknown /solutions/:segment) in place of the old silent
// `Navigate to="/"` soft-404. The edge still serves 200 + the SPA shell for unknown keys, so
// the explicit robots noindex below is the crawl signal; the /app/* shell keeps its own
// in-app NotFound. Recovery links only name real routes — never a guessed destination.
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Compass, IndianRupee, LayoutGrid, LifeBuoy, MessageCircle } from "lucide-react";
import { MarketingTopBar, SiteFooter } from "./shared";
import MarketingSeo from "./MarketingSeo";
import ButtonLink from "./ButtonLink";
import { CTA_PRIMARY_LABEL } from "./navigation";

const RECOVERY_LINKS = [
  { to: "/modules", label: "Module catalogue", desc: "Every workspace, module by module", icon: LayoutGrid },
  { to: "/pricing", label: "Plans & pricing", desc: "Plans, the price calculator and a full feature comparison", icon: IndianRupee },
  { to: "/help", label: "Help Center", desc: "Guides & answers", icon: LifeBuoy },
  { to: "/contact", label: "Contact", desc: "Sales, onboarding & support channels", icon: MessageCircle },
];

export default function MarketingNotFound() {
  const location = useLocation();
  return (
    <div className="landing-page relative min-h-screen bg-surface flex flex-col">
      <MarketingSeo
        title="Page not found — Vidyapeeth360"
        description="The page you were looking for doesn't exist. Explore Vidyapeeth360's modules, pricing, help and contact pages instead."
        path={location.pathname}
        robots="noindex"
      />
      <MarketingTopBar />
      <main id="main" className="flex-1" data-testid="marketing-404">
        <section className="mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 sm:py-24">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-accent/10 text-accent-active">
            <Compass className="h-7 w-7" aria-hidden="true" />
          </span>
          <p className="mt-6 font-heading text-[12px] font-semibold uppercase tracking-[0.2em] text-ink-subtle">Error 404</p>
          <h1 className="mt-2 text-balance font-heading text-3xl font-bold tracking-[-0.03em] text-ink sm:text-4xl">
            We couldn&rsquo;t find that page.
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-base leading-7 text-ink-2">
            The address{" "}
            <code className="rounded bg-muted px-1.5 py-0.5 text-[0.9em] text-ink break-all">{location.pathname}</code>{" "}
            doesn&rsquo;t match a live page — it may have moved, or the link may have a typo. Here&rsquo;s the way back in.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <ButtonLink to="/" data-testid="notfound-home" className="!rounded-full !px-6">
              Back to the homepage <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </ButtonLink>
            <ButtonLink to="/register" variant="outline" data-testid="notfound-trial" className="!rounded-full !px-6">
              {CTA_PRIMARY_LABEL}
            </ButtonLink>
          </div>
          <ul className="mx-auto mt-12 grid max-w-2xl gap-3 text-left sm:grid-cols-2">
            {RECOVERY_LINKS.map(({ to, label, desc, icon: Icon }) => (
              <li key={to}>
                <Link
                  to={to}
                  className="flex items-start gap-3 rounded-2xl border border-line bg-surface p-4 transition-[border-color,background-color] duration-150 hover:border-accent/30 hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring/55"
                >
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-accent/10 text-accent-active">
                    <Icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-heading text-sm font-semibold text-ink">{label}</span>
                    <span className="mt-0.5 block text-[13px] leading-5 text-ink-subtle">{desc}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
